import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchSuggestions,
  PlaceSuggestion,
  ResolvedPlace,
  resolvePlace,
} from '../util/places-service'

type Options = {
  initialValue?: string
  onPlaceSelected: (place: ResolvedPlace | null) => void
}

// Wait this long after the last keystroke before issuing a suggestion request,
// so a typist's mid-word characters collapse into a single network call.
const SUGGESTION_DEBOUNCE_MS = 500

// Owns the controlled input value, suggestions list, keyboard/active-index state, and selection flow for the new-API autocomplete.
export function usePlaceAutocomplete({ initialValue = '', onPlaceSelected }: Options) {
  const [inputValue, setInputValue] = useState(initialValue)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const requestCounter = useRef(0)
  const suppressNextFetch = useRef(false)

  useEffect(() => {
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false
      return
    }
    if (!inputValue) {
      setSuggestions([])
      setActiveIndex(-1)
      return
    }
    const handle = setTimeout(() => {
      requestCounter.current += 1
      const requestId = requestCounter.current
      fetchSuggestions(inputValue).then((next) => {
        if (requestId !== requestCounter.current) return
        setSuggestions(next)
        setActiveIndex(next.length > 0 ? 0 : -1)
      })
    }, SUGGESTION_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [inputValue])

  // Hides the listbox without mutating the input value.
  const clearSuggestions = useCallback(() => {
    setSuggestions([])
    setActiveIndex(-1)
  }, [])

  // Sets the input value from an external source (e.g. parent address prop) without retriggering a suggestion fetch — guards against the post-selection refetch loop.
  const syncInputValue = useCallback((value: string) => {
    requestCounter.current += 1
    suppressNextFetch.current = true
    setInputValue(value)
    setSuggestions([])
    setActiveIndex(-1)
  }, [])

  // Commits a suggestion: optimistically fills the input, hides the list, resolves the place detail, and notifies the consumer.
  const selectSuggestion = useCallback(
    async (suggestion: PlaceSuggestion) => {
      requestCounter.current += 1
      suppressNextFetch.current = true
      // Optimistic fill: combine primary + secondary so the input matches what the user just clicked
      // (the parent will overwrite with the fully-formatted address once resolvePlace resolves).
      const optimisticValue = suggestion.secondaryText
        ? `${suggestion.primaryText}, ${suggestion.secondaryText}`
        : suggestion.primaryText
      setInputValue(optimisticValue)
      clearSuggestions()
      const resolved = await resolvePlace(suggestion.placeId)
      onPlaceSelected(resolved)
    },
    [clearSuggestions, onPlaceSelected],
  )

  return {
    inputValue,
    setInputValue,
    suggestions,
    activeIndex,
    setActiveIndex,
    clearSuggestions,
    syncInputValue,
    selectSuggestion,
  }
}
